import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle2, Palmtree, Star } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function ActivitySuccess() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const { t } = useTranslation();
  const [status, setStatus] = useState("checking");
  const [req, setReq] = useState(null);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [reviewed, setReviewed] = useState(false);
  const attempts = useRef(0);

  useEffect(() => {
    const sid = sp.get("session_id");
    if (!sid) { setStatus("error"); return; }
    const poll = async () => {
      try {
        const { data } = await api.get(`/activity-requests/status/${sid}`);
        setReq(data);
        if (data.reviewed) setReviewed(true);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
      } catch (e) { console.error("activity status poll failed", e); }
      attempts.current += 1;
      if (attempts.current < 8) setTimeout(poll, 2000);
      else setStatus("timeout");
    };
    poll();
  }, [sp]);

  const submitReview = async () => {
    if (rating < 1) { toast.error(t("activities.rate")); return; }
    try {
      await api.post("/activity-reviews", { request_id: req.request_id, email: req.email, rating, comment });
      setReviewed(true);
      toast.success(t("activities.reviewThanks"));
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <div className="section-sand min-h-[70vh] flex items-center justify-center py-16 px-4">
      <div data-testid="activity-success" className="bg-white rounded-3xl shadow-card border border-border p-10 max-w-md w-full text-center animate-fade-up">
        {status === "checking" && <p className="text-canard font-body py-10">{t("payment.processing")}</p>}
        {status === "paid" && (
          <>
            <div className="w-20 h-20 rounded-full bg-emeraude/15 flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="w-11 h-11 text-emeraude" />
            </div>
            <h1 className="font-heading font-extrabold text-2xl text-ink">{t("activities.paySuccess")}</h1>
            <p className="text-muted2 font-body mt-3">{t("activities.paySuccessText")}</p>
            <div className="bg-sand rounded-xl p-4 mt-6 text-left text-sm font-body space-y-1">
              <p><span className="text-muted2">{t("activities.reqTitle")}:</span> <span className="font-bold text-canard-dark">{req?.activity_name}</span></p>
              <p><span className="text-muted2">{t("activities.people")}:</span> <span className="font-bold">{req?.people}</span></p>
              <p><span className="text-muted2">{t("checkout.total")}:</span> <span className="font-bold">{req?.amount} €</span></p>
            </div>

            {!reviewed ? (
              <div data-testid="activity-review-form" className="mt-6 text-left border-t border-border pt-5">
                <p className="font-heading font-bold text-ink text-sm mb-2">{t("activities.rate")}</p>
                <div className="flex gap-1 mb-3">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <button key={s} data-testid={`activity-star-${s}`} onClick={() => setRating(s)} type="button">
                      <Star className={`w-7 h-7 ${s <= rating ? "fill-emeraude text-emeraude" : "text-border"}`} />
                    </button>
                  ))}
                </div>
                <textarea data-testid="activity-review-comment" value={comment} onChange={(e) => setComment(e.target.value)} rows={2} placeholder={t("activities.reviewComment")} className="w-full rounded-xl bg-sand p-3 text-sm" />
                <Button data-testid="activity-review-submit" onClick={submitReview} className="w-full mt-2 rounded-full bg-emeraude hover:bg-emeraude-dark font-bold h-11">{t("activities.submitReview")}</Button>
              </div>
            ) : (
              <p data-testid="activity-reviewed" className="mt-6 text-emeraude-dark font-bold text-sm border-t border-border pt-5">{t("activities.reviewThanks")}</p>
            )}

            <div className="flex gap-2 mt-6">
              <Button variant="outline" onClick={() => nav("/")} className="flex-1 rounded-full">{t("payment.backHome")}</Button>
              <Button data-testid="activity-back-list" onClick={() => nav("/activites")} className="flex-1 rounded-full bg-canard hover:bg-canard-dark">{t("activities.title")}</Button>
            </div>
          </>
        )}
        {(status === "timeout" || status === "error") && (
          <>
            <Palmtree className="w-12 h-12 text-canard mx-auto mb-4" />
            <p className="text-muted2 font-body">{t("payment.processing")}</p>
            <Button onClick={() => nav("/activites")} className="mt-6 rounded-full bg-canard">{t("activities.title")}</Button>
          </>
        )}
      </div>
    </div>
  );
}
